if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        progress: this.querySelector('[data-gallery-progress]'),
        progressThumb: this.querySelector('[data-gallery-progress-thumb]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      this.progressFrame = null;
      this.onProgressChange = this.requestProgressUpdate.bind(this);
      this.initProgressScrollbar();
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    disconnectedCallback() {
      const slider = this.elements.viewer?.slider;
      if (slider) {
        slider.removeEventListener('scroll', this.onProgressChange);
        slider.removeEventListener('load', this.onProgressChange, true);
        slider.style.removeProperty('height');
      }
      this.mql.removeEventListener('change', this.onProgressChange);
      if (this.progressResizeObserver) this.progressResizeObserver.disconnect();
      if (this.progressMutationObserver) this.progressMutationObserver.disconnect();
      if (this.progressFrame) window.cancelAnimationFrame(this.progressFrame);
    }

    initProgressScrollbar() {
      const slider = this.elements.viewer?.slider;
      if (!slider || !this.elements.progress || !this.elements.progressThumb) return;

      slider.addEventListener('scroll', this.onProgressChange, { passive: true });
      slider.addEventListener('load', this.onProgressChange, true);
      this.mql.addEventListener('change', this.onProgressChange);

      if ('ResizeObserver' in window) {
        this.progressResizeObserver = new ResizeObserver(this.onProgressChange);
        this.progressResizeObserver.observe(slider);
        this.progressResizeObserver.observe(this.elements.progress);
        slider.querySelectorAll('.product__media').forEach((media) => {
          this.progressResizeObserver.observe(media);
        });
      }

      this.progressMutationObserver = new MutationObserver(this.onProgressChange);
      this.progressMutationObserver.observe(slider, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      this.requestProgressUpdate();
    }

    getVisibleMediaSlides() {
      const slider = this.elements.viewer?.slider;
      if (!slider) return [];

      return Array.from(slider.querySelectorAll('.product__media-item')).filter((slide) => {
        return !slide.classList.contains('hidden') && window.getComputedStyle(slide).display !== 'none';
      });
    }

    requestProgressUpdate() {
      if (!this.elements.progress || !this.elements.progressThumb) return;
      if (this.progressFrame) window.cancelAnimationFrame(this.progressFrame);
      this.progressFrame = window.requestAnimationFrame(() => this.updateProgressScrollbar());
    }

    updateProgressScrollbar() {
      const slider = this.elements.viewer?.slider;
      if (!slider) return;

      const visibleSlides = this.getVisibleMediaSlides();
      const slideCount = visibleSlides.length;
      this.updateMobileGalleryHeight(visibleSlides);
      const maximumScroll = Math.max(slider.scrollWidth - slider.clientWidth, 0);
      const shouldHide = this.mql.matches || slideCount < 2 || maximumScroll < 1;

      this.elements.progress.hidden = shouldHide;
      if (shouldHide) return;

      const thumbWidth = Math.min((slider.clientWidth / slider.scrollWidth) * 100, 100);
      const scrollProgress = Math.min(Math.abs(slider.scrollLeft) / maximumScroll, 1);
      const maximumTravel = this.elements.progress.clientWidth * (1 - thumbWidth / 100);

      this.elements.progressThumb.style.width = `${thumbWidth}%`;
      this.elements.progressThumb.style.transform = `translate3d(${scrollProgress * maximumTravel}px, 0, 0)`;
    }

    updateMobileGalleryHeight(visibleSlides) {
      const slider = this.elements.viewer?.slider;
      if (!slider) return;

      if (this.mql.matches || visibleSlides.length === 0) {
        slider.style.removeProperty('height');
        return;
      }

      const sliderBounds = slider.getBoundingClientRect();
      const sliderCenter = sliderBounds.left + sliderBounds.width / 2;
      let activeSlide = visibleSlides[0];
      let closestDistance = Number.POSITIVE_INFINITY;

      visibleSlides.forEach((slide) => {
        const slideBounds = slide.getBoundingClientRect();
        const slideCenter = slideBounds.left + slideBounds.width / 2;
        const distance = Math.abs(slideCenter - sliderCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          activeSlide = slide;
        }
      });

      const activeMedia = activeSlide.querySelector('.product__media') || activeSlide;
      const activeHeight = Math.ceil(activeMedia.getBoundingClientRect().height);

      if (activeHeight > 0) slider.style.height = `${activeHeight}px`;
    }

    onSlideChanged(event) {
      this.requestProgressUpdate();
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
        this.requestProgressUpdate();
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
